/**
 * Manages storage.
 *
 * @module app/components/StorageProvider
 */
import _ from 'lodash';
import localForage from 'localforage';
import MessageBusTopics from '../../common/MessageBusTopics';

/**
 * Defaults
 *
 * @type {Object}
 */
var defaults = {
    'sort': 'competition',
    'firstTimeJourney': JSON.stringify({
        'HIGHLIGHTS': 'true',
        'MEETINGS': 'true',
        'HOME': 'true',
    }),
    'welcomeMessages': JSON.stringify({
        'authenticated': 0,
        'nonauthenticated': 0,
    }),
};

/**
 * Local storage object.
 *
 * @type {Object}
 */
/*jshint -W079 */
let localStorage = null;

/**
 * Returns Sportsbook Object from localStorage
 *
 * @returns {Object}
 *
 * @private
 */
var getSportsbook = function () {

    return JSON.parse(localStorage.getItem('Sportsbook'));
};

/**
 * Configures and publishes localForage
 */
var publishLocalForage = function publishLocalForage() {

    localForage.config({
        name: 'Sportsbook',
    });

    window.WH.messageBus.publishSticky(MessageBusTopics.LOCAL_FORAGE, localForage);
};

/**
 * Get the local storage implementation.
 *
 * @returns {Object}
 *
 * @private
 */
var getLocalStorage = function () {

    try {
        window.localStorage.setItem('storage', '');
        window.localStorage.removeItem('storage');
        localStorage = window.localStorage;
    } catch (e) {
        localStorage = new LocalStorageFallback();
    }
};

/**
 * Shim for localStorage.
 *
 * @returns {void}
 *
 * @private
 */
var LocalStorageFallback = function () {

    /**
     * Local object to simulate browser storage.
     *
     * @type {Object}
     *
     * @private
     */
    var structureLocalStorage = {};

    /**
     * Add an item to the storage.
     *
     * @param {String} key
     * @param {Mixed} value
     *
     * @returns {void}
     *
     * @public
     */
    this.setItem = function (key, value) {

        structureLocalStorage[key] = value;
    };

    /**
     * Get an item from the storage.
     *
     * @param {String} key
     *
     * @returns {Mixed}
     *
     * @public
     */
    this.getItem = function (key) {

        if (typeof structureLocalStorage[key] !== 'undefined') {
            return structureLocalStorage[key];
        } else {
            return null;
        }
    };

    /**
     * Remove an item from the storage.
     *
     * @param {String} key
     *
     * @returns {void}
     *
     * @private
     */
    this.removeItem = function (key) {

        structureLocalStorage[key] = null;
        delete structureLocalStorage[key];
    };
};

/**
 * Initialize localStorage
 *
 * @returns {void}
 *
 * @private
 */
var initializeLocalStorage = function () {

    /**
     * Set initial Sportsbook if empty
     *
     * @returns {void}
     *
     * @private
     */
    var setInitialSportsbook = function () {

        if (!localStorage.getItem('Sportsbook')) {
            localStorage.setItem('Sportsbook', JSON.stringify({}));
        }
    };

    if (localStorage === null) {
        getLocalStorage();
        setInitialSportsbook();
    }
};

/**
 * Public functions and data.
 *
 * @type {Object}
 *
 * @private
 */
var api = {

    /**
     * Retrieves user preference based on key.
     *
     * @param {String} key
     *
     * @returns {Mixed}
     *
     * @public
     */
    get: function (key) {

        var sportsbook = getSportsbook();

        if (_.isUndefined(sportsbook[key])) {
            api.set(key, defaults[key]);
            return defaults[key];
        }

        return sportsbook[key];
    },

    /**
     * Sets a user preference based on a key
     *
     * @param {String} key
     * @param {Object/String} value
     *
     * @returns {void}
     *
     * @public
     */
    set: function (key, value) {

        var pref = {};
        pref[key] = value;

        localStorage.setItem(
            'Sportsbook',
            JSON.stringify(_.extend(getSportsbook(), pref))
        );
    },
};

initializeLocalStorage();

publishLocalForage();

export default api;
